import { ReactNode } from 'react';
import { Type, TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';
import { builtin } from 'src/presentation/components/pages/sidekick/threads/actions/utils';

export enum RuleFieldProperty {
  length = 'has length',
}

export interface SelectOption {
  disabled?: boolean;
  label?: string | ReactNode;
  optGroup?: string;
  text?: string;
  type?: string;
  value?: string | number | boolean;
}

// Guarantee that we always have fields to pick from, set by default to
// the first memory alphabetically, with every field for that memory if
// it's an object populated and selected alphabetically as well.
export const initialFields = (
  fields: string[],
  extActionMemNames: string[],
  types: Record<string, TypeSpec>,
  definedMemories: Record<string, string>,
  includeOnlyNestedProps: boolean
): { fields: string[]; lastSpec?: TypeSpec } => {
  if (fields.length === 0) {
    fields.push('');
    return { fields, lastSpec: undefined };
  }

  let typeSpec = fieldType(fields, extActionMemNames, types, definedMemories);
  if (typeSpec == null) {
    // This happens on page load before types are retrieved.
    return { fields, lastSpec: undefined };
  }
  if (typeSpec.type === Type.array) {
    const items = typeSpec.items;
    if (items == null) {
      throw new Error('null array items');
    }
    if (builtin(items.type) && items.type !== Type.object) {
      return { fields, lastSpec: typeSpec };
    }
    if (fields[fields.length - 1] !== '') {
      if (includeOnlyNestedProps) {
        const objOrArrayProp = Object.keys(items.props || {}).filter(
          (prop: any) => prop.type === Type.object || prop.type === Type.array
        );
        if (!objOrArrayProp.length) {
          return { fields, lastSpec: typeSpec };
        }
      }

      fields.push('');
      typeSpec = undefined;
    }
    return { fields, lastSpec: typeSpec };
  }
  if (typeSpec.type === Type.object && fields[fields.length - 1] !== '') {
    if (includeOnlyNestedProps) {
      const objOrArrayProp = Object.keys(typeSpec.props || {}).filter(
        (prop: any) => prop.type === Type.object || prop.type === Type.array
      );
      if (objOrArrayProp.length > 0) {
        return { fields, lastSpec: typeSpec };
      }
    }

    fields.push('');
    typeSpec = undefined;
  }
  return { fields, lastSpec: typeSpec };
};

export const fieldType = (
  fields: string[],
  extActionMemNames: string[],
  types: Record<string, TypeSpec>,
  definedMemories: Record<string, string>
): TypeSpec | undefined => {
  const derefCustomType = (spec: TypeSpec): TypeSpec => {
    if (builtin(spec.type)) {
      return spec;
    }
    const newSpec = types[spec.type];
    if (newSpec == null) {
      throw new Error(`missing custom type ${spec.type}`);
    }
    return newSpec;
  };
  const fieldSpec = (spec: TypeSpec, field: string): TypeSpec => {
    spec = derefCustomType(spec);
    if (spec.type === Type.array) {
      if (spec.items == null) {
        throw new Error(`null items for array spec at ${field}`);
      }

      // Recursively iterate through arrays until we find the
      // underlying object. It must be an object inside the array
      // because we're accessing a field on it.
      return fieldSpec(spec.items, field);
    }
    if (field === '') {
      return spec;
    }
    if (spec.type !== 'object') {
      throw new Error(`cannot access field ${field} of ${spec.type}`);
    }
    if (spec.props == null) {
      throw new Error(`null props for field ${field} of ${spec.type}`);
    }

    const match = field.match(/items\[(\d+)/);
    const f = match ? field.replaceAll(`[${match[1]}]`, '') : field;

    let newSpec = spec.props[f];
    if (newSpec == null) {
      // If we are using custom data, we need to do a little more work
      // if the spec is an array since it's not an FNArray. We check
      // if the first field appears in the array of external action
      // memory names. If it is and the field we are checking is "length"
      // we need to manually set the new spec to {type: "number"}.
      const memName = fields[0];
      if (memName == null) {
        // If for some reason this is null, just throw the original
        // error.
        throw new Error(`prop ${field} does not exist on spec`);
      }

      let isExtActionMem = extActionMemNames.includes(memName);
      if (memName.endsWith('_first_')) {
        isExtActionMem = extActionMemNames.includes(memName.replace('_first_', ''));
      }

      if (isExtActionMem) {
        if (field === RuleFieldProperty.length) {
          newSpec = { type: Type.number };
          return newSpec;
        }
      }
      throw new Error(`prop ${field} does not exist on spec`);
    }
    newSpec = derefCustomType(newSpec);
    return newSpec;
  };

  if (fields.length === 0) {
    return;
  }
  if (fields[0] == null) {
    throw new Error('null field');
  }

  // Special case where we have a field that hasn't been set yet, such as
  // for a newly added rule.
  if (fields.length === 1 && fields[0] === '') {
    return;
  }
  const memType = definedMemories[fields[0]];
  if (memType == null) {
    return; // This happens when mems haven't loaded yet.
  }
  let spec = types[memType];
  if (spec == null) {
    // This happens when types haven't loaded yet.
    return;
  }
  spec = derefCustomType(spec);
  if (fields.length === 1) {
    return spec;
  }

  // Skip the first field which is the memory name, and stop before the
  // last field, as that is handled separately, since there's no
  // requirement it has properties.
  for (let i = 1; i < fields.length - 1; ++i) {
    const field = fields[i];
    if (field == null) {
      throw new Error('null field');
    }
    spec = fieldSpec(spec, field);
  }

  const field = fields[fields.length - 1];
  if (field == null) {
    throw new Error('null last field');
  }
  return fieldSpec(spec, field);
};

export const getAllMemoriesFields = (definedMemories: Record<string, string>) => {
  const mems: string[] = [];
  for (const mem in definedMemories) {
    mems.push(mem);
  }
  mems.sort((a: string, b: string): number => a.localeCompare(b));
  return mems;
};

export const getDefMemoriesOptionsNext = (
  fields: string[],
  extActionMemNames: string[],
  types: Record<string, TypeSpec>,
  definedMemories: Record<string, string>,
  includeOnlyNestedProps: boolean
) => {
  const arr: string[] = [];

  if (fields.length === 0) {
    try {
      Object.keys(definedMemories).forEach(mem => {
        const optGroups = getOptionGroup([mem, ''], extActionMemNames, types, definedMemories, includeOnlyNestedProps);

        arr.push(
          ...handleLevel(0, [mem], optGroups, {
            extActionMemNames,
            types,
            definedMemories,
            includeOnlyNestedProps,
          })
        );
      });
    } catch (e) {
      return arr;
    }
  }
  return arr;
};

const handleLevel = (level, priorValue, priorOptGroups, options) => {
  const { extActionMemNames, types, definedMemories, includeOnlyNestedProps } = options;

  const arr: string[] = [];

  if (priorOptGroups.length > level && priorOptGroups[level].length > 0) {
    priorOptGroups[level].forEach(({ value }: any) => {
      const optGroup2 = getOptionGroup(
        [...priorValue, value, ''],
        extActionMemNames,
        types,
        definedMemories,
        includeOnlyNestedProps,
        true
      );

      arr.push(...handleLevel(level + 1, [...priorValue, value], optGroup2, options));
    });
  } else {
    arr.push(priorValue.join('.'));
  }
  return arr;
};

export const getOptionGroup = (
  fields: string[],
  extActionMemNames: string[],
  types: Record<string, TypeSpec>,
  definedMemories: Record<string, string>,
  includeOnlyNestedProps: boolean,
  addIndexToArrayProps = false
) => {
  const optGroups: SelectOption[][] = [];
  for (let i = 0; i < fields.slice(1).length; ++i) {
    const opts: SelectOption[] = [];

    let ft;
    try {
      ft = fieldType(fields.slice(0, i + 1), extActionMemNames, types, definedMemories);
    } catch (e) {
      ft = null;
    }
    if (ft == null) {
      optGroups.push(opts);
      continue;
    }
    if (includeOnlyNestedProps && ft.type !== Type.object && ft.type !== Type.array) {
      continue;
    }

    // If we have a custom memory that's an array, we need to populate
    // the options with an added 'optGroup' property

    const memName = fields[0];
    let isExtActionMem = extActionMemNames.includes(memName);
    if (memName.endsWith('_first_')) {
      isExtActionMem = extActionMemNames.includes(memName.replace('_first_', ''));
    }

    if (isExtActionMem && ft.type === Type.array) {
      ft = ft.items;
      if (ft == null) {
        throw new Error('null items for array');
      }
      if (ft.type === Type.object) {
        for (const prop of Object.keys(ft.props || {})) {
          opts.push({
            label: prop,
            value: prop,
            optGroup: 'Fields',
          });
        }
        const propOpt: SelectOption = {
          label: 'length',
          value: RuleFieldProperty.length,
          optGroup: 'Properties',
        };
        opts.push(propOpt);
        optGroups.push(opts);
      }
      continue;
    }

    while (ft.type === Type.array) {
      ft = ft.items;
      if (ft == null) {
        throw new Error('null items for array');
      }
    }
    if (ft.type === Type.object) {
      if (!ft.props) {
        continue;
      }

      for (const prop of Object.keys(ft.props)) {
        const isBuiltinType = builtin(ft.props[prop].type);
        const fieldType = types[ft.props[prop].type];

        const propType = isBuiltinType ? ft.props[prop].type : fieldType?.type;

        if (includeOnlyNestedProps && propType !== Type.object && propType !== Type.array) {
          continue;
        }

        let property = prop;
        if (addIndexToArrayProps && propType === Type.array) {
          property += '[0]';
        }
        opts.push({
          label: property,
          value: property,
        });
      }
      optGroups.push(opts);
      continue;
    }

    // We don't want to create options to select built-in types, like
    // "string".
    // optGroups.push(sortOpts(opts));
    optGroups.push(opts);
  }

  return optGroups;
};

export const replaceIndexes = (s: string) => {
  const regex = /\[\d+/g;
  const matchFilter = s.match(regex);
  if (matchFilter) {
    matchFilter.forEach((match: string) => {
      s = s.replace(match, '[0');
    });
    return s.toLowerCase().trim();
  }
  return s.toLowerCase().trim();
};
